import classNames from 'classnames';
import React from 'react';
import Select from 'react-select';

import BaseGroupComponent from 'components/groups/BaseGroup';
import ErrorsComponent from 'shared/Errors/components/Errors';
import { customSelectStyles } from 'components/elements/Search';
import SelectOptionArray from 'types/model/selectOption';
import { IField } from './IField';

interface Option {
  value: string;
  label: string;
  isDisabled?: boolean;
}

export interface ControlledSearchSelectGroupProps {
  field?: IField;
  namespace?: string;
  name?: string;
  options?: SelectOptionArray;
  overrideOptions?: SelectOptionArray;
  value?: string | number | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  showAsterisk?: boolean;
  label?: string;
  placeholder?: string;
  emitHiddenInput?: boolean;
  acceptEmptyValue?: boolean;
  errors?: string[];
  changeIndicated?: boolean;
  'data-cy'?: string;
}

// PLA-1040: searchable (typeahead) drop-in replacement for ControlledSelectGroup.
// Fully controlled so external resets (e.g. clearing the mark on origin change)
// are reflected, and it submits through a hidden input like the native select.
export const ControlledSearchSelectGroup = ({
  field,
  namespace,
  name,
  options,
  overrideOptions,
  value,
  onChange,
  disabled,
  required,
  label,
  placeholder,
  emitHiddenInput = true,
  errors,
  changeIndicated,
  ...rest
}: ControlledSearchSelectGroupProps) => {
  const sourceOptions = options || overrideOptions || field?.options;

  // Mirror ControlledSelectGroup's null-guard so a field the server omitted or
  // marked not-visible renders nothing (no empty/unlabeled "No options" select).
  // Fieldless mode (e.g. customer/lead) requires an EXPLICIT `options` prop:
  // `overrideOptions` alone does NOT enable rendering, because a field the server
  // omitted may still receive `overrideOptions=[]` (e.g. filterOptionsByShipperCountry
  // coerces undefined→[] for shipper_port on SPOT contracts), which must render
  // nothing — just like the native ControlledSelectGroup did for `field == undefined`.
  if (field === undefined) {
    if (options === undefined) {
      return null;
    }
  } else if (!field.visible || field.options === undefined) {
    return null;
  }

  const mappedOptions: Option[] = (sourceOptions || []).map((option) => ({
    value: String(option.value ?? option.id),
    label: option.name,
    isDisabled: option.disabled,
  }));

  const resolvedValue =
    value !== undefined && value !== null && value !== ''
      ? String(value)
      : !rest['acceptEmptyValue'] && field?.value
      ? String(field.value)
      : '';

  const selectedOption =
    mappedOptions.find((option) => option.value === resolvedValue) || null;

  // i18n label/placeholder, mirroring ControlledSelectGroup's convention.
  const namespacePlural =
    namespace && namespace.slice(-1) === 'y'
      ? `${namespace.slice(0, -1)}ies`
      : namespace
      ? `${namespace}s`
      : '';
  const fieldKey = field ? field.name.replace('_id', '') : '';
  const resolvedLabel =
    label ??
    (field && namespace
      ? I18n.translate(`${namespacePlural}.form.${fieldKey}`)
      : undefined);
  const resolvedPlaceholder =
    placeholder ??
    (field && namespace
      ? I18n.translate(`${namespacePlural}.form.select_${fieldKey}`)
      : undefined);

  const submissionName =
    name || (field && namespace ? `${namespace}[${field.name}]` : undefined);

  const isDisabled = disabled ?? (field ? !field.enabled : false);
  const isRequired = required ?? field?.required ?? false;
  // BaseGroup is the single source of the asterisk (this component never appends
  // '*' to the label), so `showAsterisk` is a legacy no-op here: show it iff required.
  const shouldShowAsterisk = isRequired;

  const handleChange = (option: Option | null) => {
    onChange?.(option ? option.value : '');
  };

  const dataCy = rest['data-cy'] || (field ? field.name : undefined);

  // PLA-1055: el resaltado de "campo cambiado" en la aprobación de una contract version.
  // El wrapper del react-select lleva la clase `.select`, que ya trae el borde azul de
  // form-base, así que sin esto un puerto/bodega cambiado se veía idéntico a uno intacto.
  const isChangeIndicated = changeIndicated ?? field?.changeIndicated;

  return (
    <div data-cy={dataCy} className='search-select'>
      <BaseGroupComponent
        label={resolvedLabel}
        required={shouldShowAsterisk}
        changeIndicated={isChangeIndicated}
        errors={errors && <ErrorsComponent errors={errors} />}
      >
        <Select
          className={classNames('select', 'react-select', {
            'input--change-indicated': isChangeIndicated,
          })}
          classNamePrefix='react-select'
          styles={customSelectStyles}
          options={mappedOptions}
          value={selectedOption}
          onChange={(option) => handleChange(option as Option | null)}
          isDisabled={isDisabled}
          isClearable
          isSearchable
          placeholder={resolvedPlaceholder}
          aria-label={resolvedLabel}
        />

        {emitHiddenInput && submissionName && (
          <input type='hidden' name={submissionName} value={resolvedValue} />
        )}

        {/* HTML5 required validation shim: react-select has no native required. */}
        {isRequired && !isDisabled && (
          <input
            tabIndex={-1}
            autoComplete='off'
            aria-hidden='true'
            value={resolvedValue}
            onChange={() => undefined}
            required
            style={{
              opacity: 0,
              width: '1px',
              height: '1px',
              position: 'absolute',
              border: 0,
              padding: 0,
            }}
          />
        )}
      </BaseGroupComponent>
    </div>
  );
};

export default ControlledSearchSelectGroup;
